import pick from 'lodash/pick';
import { v4 as uuidv4 } from 'uuid';
import { vueEnv } from '@/globals';
import initSegment, { getWriteKey } from '@/lib/segment';

const SEGMENT_TRACK_URL = 'https://api.segment.io/v1/t';

export const initAppAnalytics = (integrations = {}) => {
  const segmentKey = vueEnv('segment_key');
  initSegment(segmentKey, integrations);
};

export const identify = (user) => {
  if (!user || user.sudo || !window.analytics) return false;

  const analyticsData = prepareAnalyticsData(user);
  const userId = user.id || user.email;

  const integrations = user.analytics_integrations || {};
  return analytics.identify(userId, analyticsData, { integrations });
};

export const trackEvent = (eventName, eventData = {}) => {
  const options = {};

  if (window.analytics) {
    analytics.track(eventName, eventData, options);
  }
};

// Unload-safe track — uses sendBeacon (or keepalive fetch fallback) to survive page teardown.
// Prefer trackEvent() for normal in-session calls; use this only from visibilitychange/pagehide handlers.
export const trackEventBeacon = (eventName, eventData = {}) => {
  const writeKey = getWriteKey();
  if (!writeKey) return;

  let userId, anonymousId;
  try {
    const user = window.analytics?.user?.();
    userId = user?.id?.() || undefined;
    anonymousId = user?.anonymousId?.() || undefined;
  } catch (_) {
    /* ignore — user() may not be available yet */
  }

  if (!anonymousId) {
    try {
      anonymousId = JSON.parse(localStorage.getItem('ajs_anonymous_id'));
    } catch (_) {
      /* ignore */
    }
  }

  const now = new Date().toISOString();
  const payload = JSON.stringify({
    writeKey,
    type: 'track',
    event: eventName,
    userId: userId || undefined,
    anonymousId: anonymousId || undefined,
    messageId: window.crypto?.randomUUID?.() || uuidv4(),
    timestamp: now,
    sentAt: now,
    properties: eventData,
  });

  const blob = new Blob([payload], { type: 'text/plain' });
  const queued = navigator.sendBeacon?.(SEGMENT_TRACK_URL, blob);
  if (!queued) {
    fetch(SEGMENT_TRACK_URL, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain' },
      body: payload,
    }).catch(() => {});
  }
};

export const identifyPortalContact = (profile) => {
  if (!profile || profile.meta?.tester || !window.analytics) return;

  const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
  analytics.identify(profile.id, {
    email: profile.email,
    name,
    org_id: profile.org_id,
    org_name: profile.org_name,
  });
};

const prepareAnalyticsData = (user) => {
  const getCompanyInfo = () => {
    return ['id', 'name', 'plan', 'created_at'].reduce((acc, key) => {
      acc[key] = user['org_' + key];
      return acc;
    }, {});
  };

  const getFullName = () => {
    return [user.first_name, user.last_name].join(' ');
  };

  let data = pick(user, [
    'group_type',
    'email',
    'org_id',
    'org_domain',
    'org_name',
    'aid',
    'sudo',
  ]);
  return Object.assign(data, {
    firstName: user.first_name,
    lastName: user.last_name,
    name: getFullName(),
    phone: user.mobile,
    mobile: user.mobile,
    username: getFullName(),
    company: getCompanyInfo(),
    last_web_session: Math.floor(Date.now() / 1000),
  });
};
